/**
 * /login — public landing for the PKCE flow.
 *
 * Users almost never see this — AuthGuard kicks unauthenticated users
 * straight into beginLogin(). This route exists for:
 *   - bookmarked entry
 *   - post-logout landing (AuthGuard isn't on /login)
 *   - a safe place to retry auth after an error
 *
 * Auto-triggers signIn after 300 ms so a fresh visit is one-click.
 */
import { Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext.js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function LoginPage(): JSX.Element {
  const { ready, isAuthenticated, signIn } = useAuth();
  const navigate = useNavigate();
  const [triggered, setTriggered] = useState(false);

  // If user lands here already authenticated, bounce to /gpu.
  useEffect(() => {
    if (ready && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);

  // Soft auto-redirect to OIDC after a tiny beat so UI flicker is minimal.
  useEffect(() => {
    if (!ready || isAuthenticated || triggered) return;
    const t = window.setTimeout(() => {
      setTriggered(true);
      void signIn('/');
    }, 300);
    return () => window.clearTimeout(t);
  }, [ready, isAuthenticated, triggered, signIn]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <CardTitle>tacosai admin</CardTitle>
          <CardDescription>
            Redirecting to identity provider{triggered ? '…' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            className="w-full"
            disabled={triggered}
            onClick={() => {
              setTriggered(true);
              void signIn('/');
            }}
          >
            {triggered ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Redirecting
              </>
            ) : (
              'Sign in'
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            Admin access requires <code className="rounded bg-muted px-1 py-0.5 text-[11px]">system_admin</code> or{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[11px]">owner</code> role.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
